import { z } from 'zod';
import { UuidString } from '../../primitives.js';

/**
 * State — кастомизируемая колонка канбана (per Project). Аналог Plane `State`.
 *
 * - `group` — нормализованная категория (для cross-project отчётности). Локальные
 *   `name`/`color_token` могут отличаться, но `group` всегда из 6 фиксированных.
 * - `color_token` — из палитры 12 hues из STAITER_UI.md §I.26 (`--column-color-*`).
 *
 * См. `docs/STAITER_UX.md` §11.4 и `docs/backlogs/task-module/be-backlog.md` BE-S12.1-3.
 */

/**
 * B1 (§3.2) — группа `paused` («Ожидание»).
 *
 * Задача, ждущая внешнего события, не работает и не закончена: её нельзя считать
 * просроченной и на неё не заводят новые SLA-алармы, но из роллапа она не
 * исчезает и из дефолтных фильтров тоже — работа не снята, она приостановлена.
 * Порядок в UI — между `started` и `completed` (контракт для `status-choice.ts`);
 * порядок enum и порядок отображения совпадают намеренно, чтобы не заводить
 * второй источник правды.
 *
 * Задачного «На паузе» отдельно НЕТ (решение владельца В-2): пауза проекта —
 * существующий `on_hold` проекта, это разные сущности.
 */
export const StateGroup = z.enum([
  'backlog',
  'unstarted',
  'started',
  'paused',
  'completed',
  'cancelled',
  'triage',
]);
export type StateGroup = z.infer<typeof StateGroup>;

export const StateColorToken = z.enum([
  'slate',
  'blue',
  'cyan',
  'teal',
  'green',
  'lime',
  'yellow',
  'amber',
  'orange',
  'red',
  'pink',
  'purple',
]);
export type StateColorToken = z.infer<typeof StateColorToken>;

export const State = z.object({
  id: UuidString,
  entity_id: UuidString,
  project_id: UuidString,
  name: z.string().min(1).max(100),
  color_token: StateColorToken,
  group: StateGroup,
  /** Fractional ordering — для drag-reorder колонок без переиндексации. */
  sequence: z.number().finite(),
  /** Default state для новых задач проекта. Ровно 1 на проект (DB-constraint). */
  is_default: z.boolean(),
  /** Triage-state для auto-task от агента (отдельная семантика). */
  is_triage: z.boolean(),
  /**
   * WTP-F8 — ступень оттенка внутри группы, закреплённая за статусом
   * (решение владельца 31.08 «Закрепляем»).
   *
   * Необязательное: старый клиент поля не ждёт, а строки, пришедшие без него,
   * фронт считает порядковым фолбэком — ровно как считал до закрепления.
   */
  shade_index: z.number().int().min(0).optional(),
});
export type State = z.infer<typeof State>;
